/**
 * Bank account mutations used inside the list; each throws on a refusal (see bankAccountError)
 * and reads the list again, since a state change can move an account out of its filter.
 */
import { useCallback } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  CreatePartyBankAccountDocument,
  DeactivatePartyBankAccountDocument,
  PartyBankAccountsDocument,
  RejectPartyBankAccountDocument,
  RevokePartyBankAccountDocument,
  SettingsPendingBankAccountsDocument,
  SignPartyBankAccountDocument,
  UpdatePartyBankAccountDocument,
  type CreatePartyBankAccountInput,
  type PartyBankAccountReasonInput,
  type SignPartyBankAccountInput,
  type UpdatePartyBankAccountInput
} from '@/graphql';

// The list and the settings menu's count awaiting verification, both open in the settings shell
// whenever these run; a query named here that is not open warns.
const BANK_ACCOUNT_QUERIES = [PartyBankAccountsDocument, SettingsPendingBankAccountsDocument];

const REFETCH = {
  refetchQueries: BANK_ACCOUNT_QUERIES,
  awaitRefetchQueries: true,
  // A refusal throws instead of resolving with `error` (the app's default errorPolicy is 'all').
  errorPolicy: 'none'
} as const;

export function useBankAccountMutations() {
  const client = useApolloClient();
  const [createMutation] = useMutation(CreatePartyBankAccountDocument, REFETCH);
  const [updateMutation] = useMutation(UpdatePartyBankAccountDocument, REFETCH);
  const [signMutation] = useMutation(SignPartyBankAccountDocument, REFETCH);
  const [rejectMutation] = useMutation(RejectPartyBankAccountDocument, REFETCH);
  const [revokeMutation] = useMutation(RevokePartyBankAccountDocument, REFETCH);
  const [deactivateMutation] = useMutation(DeactivatePartyBankAccountDocument, REFETCH);

  /** Reads the open list again after a refusal; best effort, it never throws. */
  const refresh = useCallback(async () => {
    try {
      await client.refetchQueries({ include: BANK_ACCOUNT_QUERIES });
    } catch {
      // The refusal is what the reader needs; a list that could not be read again stays as it is.
    }
  }, [client]);

  return {
    refresh,
    create: async (input: CreatePartyBankAccountInput) =>
      (await createMutation({ variables: { input } })).data?.createPartyBankAccount,
    update: async (input: UpdatePartyBankAccountInput) =>
      (await updateMutation({ variables: { input } })).data?.updatePartyBankAccount,
    sign: async (input: SignPartyBankAccountInput) =>
      (await signMutation({ variables: { input } })).data?.signPartyBankAccount,
    reject: async (input: PartyBankAccountReasonInput) =>
      (await rejectMutation({ variables: { input } })).data?.rejectPartyBankAccount,
    revoke: async (input: PartyBankAccountReasonInput) =>
      (await revokeMutation({ variables: { input } })).data?.revokePartyBankAccount,
    deactivate: async (input: PartyBankAccountReasonInput) =>
      (await deactivateMutation({ variables: { input } })).data?.deactivatePartyBankAccount
  };
}
